/**
 * Sprint 6 · Semana 3.2 — Save-As-Template Dialog
 *
 * Modal reutilizable para guardar un escenario como plantilla.
 * Solicita: name (obligatorio), description, tags (chips), is_public.
 */
import { useState } from 'react';
import { templatesAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { BookmarkPlus, Loader2, X } from 'lucide-react';

export function SaveTemplateDialog({ open, onOpenChange, projectId, scenarioId, scenarioName, onSaved }) {
  const [name, setName] = useState(scenarioName ? `${scenarioName} · plantilla` : '');
  const [description, setDescription] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [tags, setTags] = useState([]);
  const [isPublic, setIsPublic] = useState(true);
  const [saving, setSaving] = useState(false);

  const addTag = () => {
    const t = tagsInput.trim();
    if (!t || tags.includes(t)) return;
    setTags([...tags, t]);
    setTagsInput('');
  };

  const removeTag = (t) => setTags(tags.filter((x) => x !== t));

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    setSaving(true);
    try {
      const { data } = await templatesAPI.create({
        project_id: projectId,
        scenario_id: scenarioId,
        name: name.trim(),
        description: description.trim(),
        tags,
        is_public: isPublic,
      });
      toast.success(`Plantilla "${data.name}" creada`);
      onSaved?.(data);
      onOpenChange(false);
      // Reset
      setName(scenarioName ? `${scenarioName} · plantilla` : '');
      setDescription('');
      setTags([]);
      setTagsInput('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo crear la plantilla');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-testid="save-template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookmarkPlus className="h-5 w-5" style={{ color: '#6366F1' }} />
            Guardar como plantilla
          </DialogTitle>
          <DialogDescription>
            Guarda este escenario como plantilla reusable. Podrás aplicarla a cualquier proyecto futuro con un click.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="tpl-name" className="text-xs">Nombre <span className="text-red-500">*</span></Label>
            <Input
              id="tpl-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Puente residencial 65% aforo"
              data-testid="tpl-name-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="tpl-desc" className="text-xs">Descripción</Label>
            <Textarea
              id="tpl-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Notas de contexto: banco, ciudad, tipo de producto..."
              className="min-h-[60px] text-sm"
              data-testid="tpl-desc-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="tpl-tags" className="text-xs">Tags</Label>
            <div className="flex gap-2">
              <Input
                id="tpl-tags"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="Presiona Enter para agregar"
                data-testid="tpl-tags-input"
              />
              <Button type="button" variant="outline" size="sm" onClick={addTag} data-testid="tpl-add-tag">
                Agregar
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tags.map((t) => (
                  <Badge key={t} variant="secondary" className="text-xs pl-2 pr-1 py-0.5 gap-1">
                    {t}
                    <button onClick={() => removeTag(t)} className="hover:bg-muted rounded-sm p-0.5">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-2.5 bg-muted/20">
            <div>
              <Label htmlFor="tpl-public" className="text-xs font-medium">Compartir con mi equipo</Label>
              <p className="text-[10px] text-muted-foreground">
                {isPublic ? 'Todos en tu company podrán usarla' : 'Solo tú podrás usarla'}
              </p>
            </div>
            <Switch id="tpl-public" checked={isPublic} onCheckedChange={setIsPublic} data-testid="tpl-public-switch" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="tpl-cancel">
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            style={{ background: '#6366F1' }}
            className="text-white hover:opacity-90"
            data-testid="tpl-save"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <BookmarkPlus className="h-4 w-4 mr-1.5" />}
            Guardar plantilla
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
